import React, { useContext, useState, useEffect } from 'react'
import { Button, Dialog } from 'components/ui'
import { AppSumoContext } from 'contexts/AppSumoContext'
import { motion } from 'framer-motion'
import { HiOutlineStar, HiStar } from 'react-icons/hi'
import { HiOutlineHeart, HiHeart } from 'react-icons/hi2'
import { BsStars } from 'react-icons/bs'

const StarRating = ({ rating, onRatingChange, size = 'large' }) => {
    const [hoverRating, setHoverRating] = useState(0)
    
    const starSize = size === 'large' ? 'text-4xl' : 'text-2xl'
    const spacing = size === 'large' ? 'gap-2' : 'gap-1'
    
    return (
        <div className={`flex items-center justify-center ${spacing}`}>
            {[1, 2, 3, 4, 5].map((star) => {
                const isActive = star <= (hoverRating || rating)
                return (
                    <motion.button
                        key={star}
                        type="button"
                        className={`${starSize} transition-all duration-200 transform hover:scale-110 focus:outline-none focus:scale-110`}
                        onClick={() => onRatingChange(star)}
                        onMouseEnter={() => setHoverRating(star)}
                        onMouseLeave={() => setHoverRating(0)}
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        whileTap={{ scale: 0.95 }}
                        animate={{
                            color: isActive ? '#FFD700' : '#D1D5DB',
                            filter: isActive ? 'drop-shadow(0 0 8px rgba(255, 215, 0, 0.6))' : 'none'
                        }}
                    >
                        {isActive ? (
                            <HiStar className="drop-shadow-lg" />
                        ) : (
                            <HiOutlineStar />
                        )}
                    </motion.button>
                )
            })}
        </div>
    )
}

const AppSumoReviewModal = () => {
    const { setShowAppSumoReviewModal } = useContext(AppSumoContext)
    const [rating, setRating] = useState(0)
    const [showThankYou, setShowThankYou] = useState(false)
    const [showRedirectMessage, setShowRedirectMessage] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [countdown, setCountdown] = useState(3)

    const handleRatingChange = (newRating) => {
        setRating(newRating)
        setIsSubmitting(true)
        
        // Simulate API call delay
        setTimeout(() => {
            setIsSubmitting(false)
            
            if (newRating >= 4) {
                // Show redirect message first
                setShowRedirectMessage(true)
                setCountdown(5)
                
                // Start countdown
                const countdownInterval = setInterval(() => {
                    setCountdown(prev => {
                        if (prev <= 1) {
                            clearInterval(countdownInterval)
                            window.open('https://appsumo.com/products/m/followr/', '_blank')
                            setShowAppSumoReviewModal(false)
                            return 0
                        }
                        return prev - 1
                    })
                }, 1000)
            } else {
                // Show thank you message
                setShowThankYou(true)
            }
        }, 800)
    }

    const handleClose = () => {
        setShowAppSumoReviewModal(false)
    }

    return (
        <Dialog
            isOpen={true}
            closable={true}
            onClose={handleClose}
            onRequestClose={handleClose}
            overlayClassName="flex w-full h-full items-center justify-center backdrop-blur-sm"
            contentClassName="!p-0 !bg-transparent"
            width={520}
        >
            <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                className="relative overflow-hidden rounded-2xl bg-white dark:bg-gray-800 shadow-2xl"
            >
                {/* AppSumo Header Banner */}
                <div className="relative h-32 overflow-hidden">
                    {/* <div className="absolute inset-0 bg-black/20"></div> */}
                    <img 
                        src="/img/others/appsumo-banner.jpg" 
                        className="w-full h-full object-cover" 
                        alt="AppSumo"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                        {/* <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.3, type: "spring", stiffness: 300 }}
                            className="text-white text-2xl font-bold flex items-center gap-2"
                        >
                            <BsStars className="text-yellow-300" />
                            AppSumo Review
                        </motion.div> */}
                    </div>
                </div>

                {!showThankYou && !showRedirectMessage ? (
                    <div className="p-8">
                        {/* Main Content */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                            className="text-center space-y-6"
                        >
                            {/* Greeting */}
                            <div className="space-y-3">
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                    Hey Sumo-ling! 👋
                                </h2>
                                <p className="text-lg text-gray-700 dark:text-gray-300 font-medium">
                                    Can you help us out? 🙌
                                </p>
                            </div>

                            {/* Description */}
                            <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6 space-y-4">
                                <p className="text-gray-600 dark:text-gray-400 text-sm">
                                    If you're enjoying Followr, leaving a quick review on AppSumo really helps us right now.
                                </p>
                                <p className="text-gray-600 dark:text-gray-400 text-sm">
                                    It only takes a minute, and it helps us keep building new tools and improving the platform!
                                </p>
                            </div>

                            {/* Star Rating */}
                            <div className="space-y-4">
                                <p className="text-gray-800 dark:text-gray-200 font-semibold">
                                    How would you rate your experience?
                                </p>
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: 0.4 }}
                                    className="py-4"
                                >
                                    <StarRating 
                                        rating={rating} 
                                        onRatingChange={handleRatingChange}
                                        size="large"
                                    />
                                </motion.div>
                                {rating > 0 && (
                                    <motion.p
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className="text-sm text-gray-600 dark:text-gray-400"
                                    >
                                        {rating === 5 && "Amazing! 🌟"}
                                        {rating === 4 && "Great! 😊"}
                                        {rating === 3 && "Good 👍"}
                                        {rating === 2 && "Okay 😐"}
                                        {rating === 1 && "Not great 😔"}
                                    </motion.p>
                                )}
                            </div>

                            {/* Loading State */}
                            {isSubmitting && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="flex items-center justify-center py-4"
                                >
                                    <div className="flex items-center gap-3 text-orange-600">
                                        <motion.div
                                            animate={{ rotate: 360 }}
                                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                        >
                                            <BsStars className="text-xl" />
                                        </motion.div>
                                        <span className="font-medium">Processing your rating...</span>
                                    </div>
                                </motion.div>
                            )}
                        </motion.div>

                        {/* Footer */}
                        <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-600 text-center">
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                Powered by AppSumo • This modal appears only once
                            </p>
                        </div>
                    </div>
                ) : showRedirectMessage ? (
                    /* Redirect Message State */
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="p-8 text-center space-y-6"
                    >
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.2, type: "spring", stiffness: 300 }}
                            className="w-20 h-20 mx-auto bg-gradient-to-br from-orange-500 to-pink-500 rounded-full flex items-center justify-center relative"
                        >
                            <BsStars className="text-3xl text-white" />
                            {/* Countdown circle */}
                            <div className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg">
                                <span className="text-sm font-bold text-orange-600">{countdown}</span>
                            </div>
                        </motion.div>
                        
                        <div className="space-y-3">
                            <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                                Awesome! 🌟
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400 text-sm">
                                We're redirecting you to AppSumo where you can leave your review.
                            </p>
                            <p className="text-gray-600 dark:text-gray-400 text-sm">
                                Please share your experience to help other Sumo-lings discover Followr!
                            </p>
                        </div>

                        {/* Loading animation with countdown */}
                        <div className="flex items-center justify-center py-4">
                            <div className="flex items-center gap-3 text-orange-600">
                                <motion.div
                                    animate={{ rotate: 360 }}
                                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                >
                                    <BsStars className="text-xl" />
                                </motion.div>
                                <span className="font-medium">Redirecting to AppSumo in {countdown}...</span>
                            </div>
                        </div>

                        <div className="flex flex-col items-center gap-3">
                            <div className="text-xs text-gray-400">
                                Opening in a new tab
                            </div>
                            <Button
                                variant="plain"
                                size="sm"
                                onClick={() => {
                                    window.open('https://appsumo.com/products/m/followr/', '_blank')
                                    setShowAppSumoReviewModal(false)
                                }}
                                className="text-orange-600 hover:text-orange-700 underline"
                            >
                                Go now →
                            </Button>
                        </div>
                    </motion.div>
                ) : (
                    /* Thank You State */
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="p-8 text-center space-y-6"
                    >
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.2, type: "spring", stiffness: 300 }}
                            className="w-20 h-20 mx-auto bg-gradient-to-br from-pink-500 to-orange-500 rounded-full flex items-center justify-center"
                        >
                            <HiHeart className="text-3xl text-white" />
                        </motion.div>
                        
                        <div className="space-y-3">
                            <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                                Thank you for your feedback! 💝
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400">
                                We appreciate you taking the time to share your thoughts. 
                                Your input helps us improve Followr for everyone!
                            </p>
                        </div>

                        <Button
                            variant="solid"
                            size="lg"
                            onClick={handleClose}
                            className="bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white font-semibold px-8 py-3 rounded-xl shadow-lg transform hover:scale-105 transition-all duration-200"
                        >
                            Continue using Followr
                        </Button>
                    </motion.div>
                )}
            </motion.div>
        </Dialog>
    )
}

export default AppSumoReviewModal 